import React from 'react';
import { useSite } from '../useSite';
import { SITE_URL } from '../routes';
import { Seo } from '../components/Seo';
import { ArrowLink, ButtonLink, Container, Overline, SectionHeading } from '../components/Primitives';
import { AudienceCards, CtaBand, FounderCards, ProcessSteps, ServiceRows } from '../components/Sections';
import { Testimonials } from '../components/Testimonials';
import { Photo } from '../components/Photo';

const contact = (telephone) => ({ '@type': 'ContactPoint', telephone, contactType: 'customer service', availableLanguage: ['English', 'Spanish'] });

export const orgJsonLd = (lang, description) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'ProfessionalService',
      '@id': `${SITE_URL}/#organization`,
      name: 'Bullish Accounting',
      alternateName: 'BULLISH Tax & Advisory',
      legalName: 'Bullish Accounting LLC',
      slogan: 'Strategy Behind Every Decision.',
      description,
      url: `${SITE_URL}/${lang}/`,
      logo: `${SITE_URL}/logo.png`,
      image: `${SITE_URL}/og-image.png`,
      email: 'lrifa@bullishaccounting.com',
      knowsLanguage: ['en', 'es'],
      contactPoint: [contact('+1-786-727-9086'), contact('+1-754-272-7823')],
      sameAs: ['https://instagram.com/bullish_accounting'],
      founder: [
        { '@type': 'Person', name: 'Leonardo Rifa' },
        { '@type': 'Person', name: 'Daniel Lariot' },
      ],
    },
    { '@type': 'WebSite', '@id': `${SITE_URL}/#website`, name: 'Bullish Accounting', alternateName: 'BULLISH Tax & Advisory', url: `${SITE_URL}/`, inLanguage: ['en', 'es'], publisher: { '@id': `${SITE_URL}/#organization` } },
  ],
});

const Hero = () => {
  const { t, p } = useSite();
  const h = t.home.hero;
  return (
    <section className="relative overflow-hidden border-b border-bone bg-bone-light" data-testid="home-hero">
      <div className="grid lg:min-h-[640px] lg:grid-cols-12">
        <div className="px-5 py-16 md:px-8 md:py-24 lg:col-span-7 lg:py-28 lg:pl-[max(2rem,calc((100vw-72rem)/2+2rem))] lg:pr-14">
          <Overline>{h.overline}</Overline>
          <h1 className="mt-7 font-serif text-[2.5rem] font-medium leading-[1.05] tracking-[-0.01em] text-kombu-green sm:text-5xl lg:text-[3.9rem]" data-testid="home-hero-title">
            {h.h1}
          </h1>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-cafe-noir/85 md:text-lg">{h.text}</p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <ButtonLink to={p('book')} testId="hero-book-btn">{t.common.bookCta}</ButtonLink>
            <ButtonLink to={p('expertise')} variant="secondary" testId="hero-expertise-btn">{t.common.exploreExpertise}</ButtonLink>
          </div>
          <dl className="mt-12 grid grid-cols-2 gap-x-6 gap-y-6 border-t border-tan pt-6 sm:grid-cols-4" data-testid="hero-values">
            {h.values.map((v) => (
              <div key={v.t}>
                <dt className="font-serif text-xl text-kombu-green">{v.t}</dt>
                <dd className="mt-1 text-[13px] leading-snug text-cafe-noir/75">{v.d}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-10 font-serif text-xl italic text-moss-green" data-testid="hero-tagline">{t.brand.tagline}</p>
        </div>
        <div className="aspect-[4/3] lg:col-span-5 lg:aspect-auto" data-testid="hero-photo">
          <Photo name="miami-hero" sizes="(min-width: 1024px) 42vw, 100vw" eager />
        </div>
      </div>
    </section>
  );
};

const Why = () => {
  const { t } = useSite();
  const w = t.home.why;
  return (
    <section className="bg-bone-light py-20 md:py-28" data-testid="why-section">
      <Container className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-4"><SectionHeading overline={w.overline} title={w.title} intro={w.intro} /></div>
        <dl className="grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:col-span-8">
          {w.items.map((it) => (
            <div key={it.title} className="border-t border-tan pt-5">
              <dt className="font-serif text-[1.4rem] leading-snug text-kombu-green">{it.title}</dt>
              <dd className="mt-2 text-[15px] leading-relaxed text-cafe-noir/75">{it.text}</dd>
            </div>
          ))}
        </dl>
      </Container>
    </section>
  );
};

export default function HomePage() {
  const { t, p, lang } = useSite();
  const s = t.seo.home;
  const hm = t.home;
  return (
    <>
      <Seo title={s.title} description={s.description} jsonLd={orgJsonLd(lang, s.description)} />
      <Hero />
      <section className="bg-white py-20 md:py-28" data-testid="serve-section">
        <Container>
          <SectionHeading overline={hm.serve.overline} title={hm.serve.title} intro={hm.serve.intro} />
          <div className="mt-12"><AudienceCards /></div>
        </Container>
      </section>
      <section className="bg-white pb-20 md:pb-28" data-testid="expertise-section">
        <Container>
          <div className="grid gap-12 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7"><SectionHeading overline={hm.expertise.overline} title={hm.expertise.title} intro={hm.expertise.intro} /></div>
            <div className="aspect-[16/10] overflow-hidden lg:col-span-5"><Photo name="meeting" sizes="(min-width: 1024px) 40vw, 100vw" /></div>
          </div>
          <div className="mt-12"><ServiceRows /></div>
        </Container>
      </section>
      <section className="border-y border-bone bg-bone-light py-16 md:py-20" data-testid="plans-teaser">
        <Container className="grid gap-10 md:grid-cols-12 md:items-center">
          <div className="aspect-[4/3] overflow-hidden md:col-span-5"><Photo name="advisory-desk" sizes="(min-width: 768px) 40vw, 100vw" /></div>
          <div className="md:col-span-7 md:pl-6">
            <SectionHeading overline={t.plansPage.teaser.overline} title={t.plansPage.teaser.title} intro={t.plansPage.teaser.text} />
            <div className="mt-8"><ButtonLink to={p('plans')} variant="secondary" testId="plans-teaser-link">{t.plansPage.teaser.cta}</ButtonLink></div>
          </div>
        </Container>
      </section>
      <Why />
      <section className="bg-kombu-green py-20 text-bone-light md:py-28" data-testid="process-section">
        <Container>
          <SectionHeading overline={hm.process.overline} title={hm.process.title} light />
          <div className="mt-14"><ProcessSteps light /></div>
          <div className="mt-12 flex flex-wrap items-center gap-8">
            <ArrowLink to={p('howWeWork')} light testId="process-engagement-link">{hm.process.cta}</ArrowLink>
            <p className="font-serif text-lg italic text-tan">{t.brand.secondary}</p>
          </div>
        </Container>
      </section>
      <section className="bg-bone-light py-20 md:py-28" data-testid="leadership-section">
        <Container>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <SectionHeading overline={hm.leadership.overline} title={hm.leadership.title} />
            <ArrowLink to={p('about')} testId="leadership-about-link">{hm.leadership.cta}</ArrowLink>
          </div>
          <div className="mt-12"><FounderCards /></div>
        </Container>
      </section>
      <Testimonials />
      <CtaBand secondaryTo={p('howWeWork')} secondaryLabel={hm.final.secondary} photo="skyline" />
    </>
  );
}
